import { useEffect, useRef, useState } from "react";
import { Bell, Check } from "lucide-react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// helpers for VAPID
function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch {}
  };

  useEffect(() => { load(); }, [user]);
  useEffect(() => { if (!user) return; const i = setInterval(load, 30000); return () => clearInterval(i); }, [user]);

  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    // detect existing push subscription
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription())
      .then(sub => setPushOn(!!sub)).catch(() => {});
  }, []);

  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      alert("Push notifications not supported in this browser");
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return;
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data: vapid } = await api.get("/notifications/vapid-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapid.publicKey),
      });
      await api.post("/notifications/push/subscribe", { subscription: sub.toJSON() });
      setPushOn(true);
    } catch (e) { console.error(e); alert("Push setup failed: " + e.message); }
  };

  const open_n = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      setItems(items.map(i => i.id === n.id ? { ...i, read: true } : i));
      setUnread(Math.max(0, unread - 1));
    }
    setOpen(false);
    if (n.link) nav(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems(items.map(i => ({ ...i, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="relative w-9 h-9 rounded-sm hover:bg-secondary/10 flex items-center justify-center text-foreground/80 hover:text-secondary" data-testid="notif-bell">
        <Bell size={18} strokeWidth={1.25} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-h-[500px] overflow-y-auto scroll-thin parchment z-50 shadow-lg" data-testid="notif-dropdown">
          <div className="p-4 border-b border-secondary/15 flex items-center justify-between">
            <span className="label-mystic">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-secondary hover:text-secondary/80" data-testid="notif-read-all">
                Mark all read
              </button>
            )}
          </div>
          {!pushOn && (
            <div className="p-3 border-b border-secondary/15 bg-primary/5">
              <button onClick={enablePush} className="w-full text-xs text-primary uppercase tracking-wider py-2" data-testid="notif-enable-push">
                ⚡ Enable browser notifications
              </button>
            </div>
          )}
          <div>
            {items.length === 0 ? (
              <div className="p-8 text-center text-foreground/50 italic text-sm">No notifications yet</div>
            ) : items.map((n) => (
              <button key={n.id} onClick={() => open_n(n)} className={`w-full text-left p-4 border-b border-secondary/10 hover:bg-secondary/5 ${!n.read ? "bg-primary/5" : ""}`} data-testid={`notif-${n.id}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-1.5 h-1.5 mt-2 bg-primary rounded-full flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-sm">{n.title}</div>
                    {n.body && <div className="text-xs text-foreground/70 mt-1 line-clamp-2">{n.body}</div>}
                    <div className="label-mystic text-[10px] mt-1">{new Date(n.createdAt).toLocaleString()}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
